import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Insights.css';
import axiosInstance from '../../interceptor/axiosInstance';
import { format } from 'date-fns';
import ReleaseResult from './ReleaseResult';
import TrustDataDownloadBtn from './TrustDataDownloadBtn';
import AnswerPDFDownload from './AnswerPDFDownload';
import SummaryPDFDownload from './SummaryPDFDownload';
import DeleteUserInTable from './DeleteUserInTable';
import BackButton from '../BackButton';

// Skeleton Loader Component
const SkeletonRow = () => (
  <tr className="insights-skeleton-row">
    <td><div className="insights-skeleton insights-skeleton-text"></div></td>
    <td><div className="insights-skeleton insights-skeleton-text"></div></td>
    <td><div className="insights-skeleton insights-skeleton-badge"></div></td>
    <td><div className="insights-skeleton insights-skeleton-date"></div></td>
    <td><div className="insights-skeleton insights-skeleton-date"></div></td>
    <td><div className="insights-skeleton insights-skeleton-text"></div></td>
    <td><div className="insights-skeleton insights-skeleton-badge"></div></td>
    <td><div className="insights-skeleton insights-skeleton-score"></div></td>
    <td><div className="insights-skeleton insights-skeleton-score"></div></td>
    <td><div className="insights-skeleton insights-skeleton-btn"></div></td>
  </tr>
);

// Empty State Component
const EmptyState = () => (
  <div className="insights-empty-state">
    <div className="insights-empty-icon">📊</div>
    <h3 className="insights-empty-title">No Test Submissions Yet</h3>
    <p className="insights-empty-message">
      This test hasn't been started or no users have submitted it yet.
      <br />
      Check back later once users begin taking the test.
    </p>
  </div>
);

const Insights = () => {
  const navigate = useNavigate()
  const location = useLocation();
  const { testId } = location.state || {}; // Access state data safely

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [releasePopup, setReleasePopup] = useState(false); // For editing popup
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Handle Edit button click
  const handleReleaseClick = () => {
    setReleasePopup(true);
  };

  useEffect(() => {
    if (testId) {
      setLoading(true);
      axiosInstance.get(`reports/test_details/${testId}/`)
        .then((response) => {
          setReport(response.data?.data);
        })
        .catch((err) => {
          setError('Failed to fetch data');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
      setError('Invalid Test ID');
    }
  }, [testId]);


  // const handleTrustNavigate = async (user_id, testId) => {
  //   setLoading(true);
  //   try {
  //     const response = await axiosInstance.post('reports/test_summary_report/', {
  //       test_id: testId,
  //       user_id: user_id,  // Fixed the incorrect 'attemptId'
  //     });
  
  //     // Navigate and send API response to next page
  //     navigate('/trust', { state: {  reportData: response.data } });
  //   } catch (error) {
  //     setError(error.response?.data?.message || 'Failed to fetch data.');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleTrustNavigate = (user_id, testId) => {
    const newTabUrl = `/trust?user_id=${user_id}&testId=${testId}`;
    window.open(newTabUrl, "_blank");
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedUsers = report?.users ? [...report.users].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let valA = a[sortConfig.key] || '';
    let valB = b[sortConfig.key] || '';
  
    // Handle date fields properly
    const dateFields = ['created_at', 'start_date_time', 'end_date_time'];
    if (dateFields.includes(sortConfig.key)) {
      valA = valA ? new Date(valA).getTime() : 0;
      valB = valB ? new Date(valB).getTime() : 0;
    }
  
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();
  
    if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  }) : [];
  

  
  const getSortArrow = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '▲' : '▼';
    }
    return '⇅';
  };

  // const handleAnswerBtnDownload = (attemptId, testId) => {
  //   navigate("/answer", { state: { attemptId, testId } });
  // };

  const handleAnswerBtnDownload = (attemptId, testId) => {
    const newTabUrl = `/answer?attemptId=${attemptId}&testId=${testId}`;
    window.open(newTabUrl, "_blank");
  };
  
  
  return (
    <div className='insights-container'>
      {/* Header */}
      <div className='insights-header'>
        <div className='insights-header-left'>
          <BackButton />
          <div className='insights-header-content'>
            <h1 className='insights-title'>
              {report?.test?.start_date_time ? format(new Date(report?.test?.start_date_time), 'dd-MM-yyyy HH:mm') : 'N/A'} Online MCQ Test - {report?.test?.name || 'N/A'}
            </h1>
            <p className='insights-subtitle'>Reports of all test takers and their attempts</p>
          </div>
        </div>
      </div>

      {/* Loading or Empty State */}
      {loading ? (
        <div className="insights-loading-section">
          <div className="insights-info-cards-skeleton">
            <div className="insights-skeleton-card"></div>
            <div className="insights-skeleton-card"></div>
          </div>
          <div className="insights-table-wrapper">
            <table className="insights-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Category</th>
                  <th>Created</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Complete/Auto</th>
                  <th>Trust Score</th>
                  <th>Score</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, idx) => (
                  <SkeletonRow key={idx} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : error ? (
        <div className="insights-error-state">
          <div className="insights-error-icon">⚠️</div>
          <h3 className="insights-error-title">Error Loading Data</h3>
          <p className="insights-error-message">{error}</p>
        </div>
      ) : !report || Object.keys(report).length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Info Cards */}
          <div className='insights-info-cards'>
            <div className='insights-info-card'>
              <p className='insights-info-card-label'>Test Type</p>
              <p className='insights-info-card-value'>Open Quiz</p>
              <p className='insights-info-card-subtitle'>{report?.test?.name || 'N/A'} Quiz</p>
            </div>

            <div className='insights-info-card'>
              <p className='insights-info-card-label'>Created on</p>
              <p className='insights-info-card-value'>
                {report?.test?.created_at ? format(new Date(report?.test?.created_at), 'dd-MM-yyyy HH:mm') : 'N/A'}
              </p>
            </div>
          </div>

          {/* Reports Section */}
          <div className='insights-reports-section'>
            <div className='insights-reports-header'>
              <div>
                <h2 className='insights-reports-title'>Reports</h2>
                <p className='insights-reports-subtitle'>Test Attempt reports of your test takers</p>
              </div>
            </div>

            <div className='insights-reports-actions'>
              <div className='insights-action-card'>
                <p className='insights-action-label'>Summary</p>
                <div className='insights-action-content'>
                  <SummaryPDFDownload testId={testId} />
                  <p className='insights-action-hint'>Click to download the table below as Excel</p>
                </div>
              </div>

              <div className='insights-action-card insights-action-card-release'>
                <p className='insights-action-label'>Release Result</p>
                <div className='insights-action-content'>
                  <button className='insights-release-btn' onClick={handleReleaseClick}>
                    🚀 Click Here
                  </button>
                  <p className='insights-action-hint'>Release the user Result</p>
                </div>
              </div>
            </div>
          </div>

          {/* Submission Summary */}
          <div className='insights-summary-section'>
            <div className='insights-summary-header'>
              <h2 className='insights-summary-title'>Submission Summary</h2>
              <p className='insights-summary-subtitle'>How many users started and submitted the test</p>
            </div>

            <div className='insights-summary-cards'>
              <div className='insights-summary-card insights-summary-card-total'>
                <p className='insights-summary-card-label'>Total Users</p>
                <p className='insights-summary-card-number'>{report?.total || 0}</p>
              </div>

              <div className='insights-summary-card insights-summary-card-completed'>
                <p className='insights-summary-card-label'>Completed</p>
                <p className='insights-summary-card-number'>{report?.users_completed || 0}</p>
              </div>

              <div className='insights-summary-card insights-summary-card-incomplete'>
                <p className='insights-summary-card-label'>Incomplete</p>
                <p className='insights-summary-card-number'>{report?.users_incomplete || 0}</p>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className='insights-table-wrapper'>
            <table className='insights-table'>
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="insights-sortable">
                    Name <span className="insights-sort-arrow">{getSortArrow('name')}</span>
                  </th>
                  <th onClick={() => handleSort('email')} className="insights-sortable">
                    Email <span className="insights-sort-arrow">{getSortArrow('email')}</span>
                  </th>
                  <th onClick={() => handleSort('category')} className="insights-sortable">
                    Category <span className="insights-sort-arrow">{getSortArrow('category')}</span>
                  </th>
                  <th onClick={() => handleSort('created_at')} className="insights-sortable">
                    Created <span className="insights-sort-arrow">{getSortArrow('created_at')}</span>
                  </th>
                  <th onClick={() => handleSort('start_date_time')} className="insights-sortable">
                    Started <span className="insights-sort-arrow">{getSortArrow('start_date_time')}</span>
                  </th>
                  <th onClick={() => handleSort('end_date_time')} className="insights-sortable">
                    Duration <span className="insights-sort-arrow">{getSortArrow('end_date_time')}</span>
                  </th>
                  <th>Complete/Auto</th>
                  <th>Trust Score</th>
                  <th>Score</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length > 0 ? (
                  sortedUsers.map((attempt) => (
                    <tr
                      key={attempt.id}
                      className={`insights-table-row ${
                        attempt?.trust_score !== null &&
                        attempt?.trust_score !== undefined &&
                        attempt?.trust_score < 20
                          ? 'insights-low-trust'
                          : ''
                      }`}
                    >
                      <td className="insights-td-name">{attempt.name || 'N/A'}</td>
                      <td className="insights-td-email">{attempt.email || 'N/A'}</td>
                      <td>
                        <span className="insights-category-badge">{attempt.category || 'N/A'}</span>
                      </td>
                      <td className="insights-td-date">
                        {attempt.start_time
                          ? format(new Date(attempt.start_time), 'dd-MM-yyyy HH:mm')
                          : 'N/A'}
                      </td>
                      <td className="insights-td-date">
                        {attempt.end_time
                          ? format(new Date(attempt.end_time), 'dd-MM-yyyy HH:mm')
                          : 'N/A'}
                      </td>
                      <td>{attempt.duration ? attempt.duration : 'N/A'}</td>
                      <td>
                        <span className="insights-submit-badge">{attempt.submit_type}</span>
                      </td>
                      <td className="insights-td-trust">
                        <div className="insights-trust-cell">
                          <span className={`insights-trust-score ${
                            attempt?.trust_score < 20 ? 'insights-trust-low' : 
                            attempt?.trust_score < 50 ? 'insights-trust-medium' : 
                            'insights-trust-high'
                          }`}>
                            {attempt?.trust_score === null || attempt?.trust_score === undefined
                              ? 'N/A'
                              : attempt?.trust_score}
                          </span>
                          <button
                            className="insights-view-btn"
                            onClick={() => handleTrustNavigate(attempt.user_id, testId)}
                          >
                            View Report
                          </button>
                        </div>
                      </td>
                      <td className="insights-td-score">
                        <div className="insights-score-cell">
                          <span className="insights-score-percentage">
                            {attempt.score !== null &&
                            attempt.score !== undefined &&
                            attempt.total_marks
                              ? attempt.total_marks !== 0
                                ? `${((attempt.score / attempt.total_marks) * 100).toFixed(2)}% (${attempt.score} / ${attempt.total_marks})`
                                : '0% (0 / 0)'
                              : 'N/A'}
                          </span>
                          <button
                            className="insights-answer-btn"
                            onClick={() => handleAnswerBtnDownload(attempt.user_id, testId)}
                          >
                            Answer Report
                          </button>
                        </div>
                      </td>
                      <td className="insights-td-delete">
                        <DeleteUserInTable attemptId={attempt.test_submitted_id} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan='10' className="insights-no-data">No data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Release Result Popup */}
      {releasePopup && (
        <div className="insights-modal-overlay" onClick={() => setReleasePopup(false)}>
          <div className="insights-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="insights-modal-close" onClick={() => setReleasePopup(false)}>
              ✕
            </button>
            <ReleaseResult
              user={report}
              onClose={() => setReleasePopup(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Insights;
